<template>
  <!-- Desktop: sticky horizontal tabs -->
  <div
    class="hidden lg:block sticky z-40 border-b border-gray-200 w-full h-[64px] transition-[top] duration-300 ease-out"
    :style="{
      top: `${contextTabsStickyTop}px`,
      background: `linear-gradient(to bottom, ${cardColor}, #ffffff)`,
    }"
  >
    <div class="max-w-[1200px] mx-auto h-full flex items-center justify-between">
      <div
        class="flex flex-col justify-center leading-0 text-[18px] whitespace-nowrap font-sans font-semibold"
      >
        <p class="leading-[28px]">{{ clinicName }}</p>
      </div>

      <div class="flex items-center h-full">
        <a
          v-for="(tab, index) in navigationTabs"
          :key="index"
          :href="tab.href"
          class="px-[16px] py-[24px] text-[14px] whitespace-nowrap font-medium border-b-2 h-full flex items-center"
          :style="tabStyle(tab, index)"
          @click="onTabClick($event, tab)"
        >
          {{ tab.label }}
        </a>
      </div>
    </div>
  </div>

  <!-- Mobile: sticky clinic context bar with expandable section links -->
  <div
    class="lg:hidden sticky z-30 border-b border-gray-200 w-full transition-[top] duration-300 ease-out"
    :style="{
      top: `${contextTabsStickyTop}px`,
      background: `linear-gradient(to bottom, ${cardColor}, #ffffff)`,
    }"
  >
    <button
      type="button"
      class="flex h-12 w-full items-center justify-between gap-3 px-4 outline-none focus:outline-none focus-visible:outline-none"
      :aria-expanded="mobileExpanded"
      @click="mobileExpanded = !mobileExpanded"
    >
      <span
        class="truncate text-left text-[14px] font-semibold leading-5 font-sans"
      >
        {{ clinicName }}
      </span>
      <svg
        class="size-5 shrink-0 text-gray-600 transition-transform duration-200"
        :class="{ 'rotate-180': mobileExpanded }"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m6 9 6 6 6-6" />
      </svg>
    </button>

    <div v-if="mobileExpanded" class="border-t border-gray-200 px-4 py-1">
      <a
        v-for="(tab, index) in navigationTabs"
        :key="index"
        :href="tab.href"
        class="block py-3 text-[14px] font-medium font-sans no-underline"
        :style="{ color: isActiveTab(tab, index) ? ACTIVE_TEXT_COLOR : INACTIVE_TEXT_COLOR }"
        @click="onTabClick($event, tab)"
      >
        {{ tab.label }}
      </a>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { themeCardColorFor } from '@/utils/siteThemeColors'
import {
  afterLayoutSettle,
  getElementScrollTop,
  isHomeAnchor,
  smoothScrollToElement,
  smoothScrollToTop,
  SMOOTH_SCROLL_MS,
} from '@/utils/scrollToAnchor'
import {
  contextTabsStickyTop,
  mainHeaderPinningActive,
  mainHeaderRevealHeight,
  mainHeaderRevealed,
} from '@/utils/stickyMainHeader'

/**
 * Extra clearance above the section for the pinned main header.
 * Section `scroll-mt` already covers the Context Navigation Tabs bar itself.
 */
const mainHeaderScrollOffset = () =>
  mainHeaderPinningActive.value ? mainHeaderRevealHeight.value : 0

const TAB_COUNT = 8

const ACTIVE_TEXT_COLOR = '#262626'
const ACTIVE_BORDER_COLOR = '#262626'
const INACTIVE_TEXT_COLOR = '#374151'
const INACTIVE_BORDER_COLOR = '#e5e7eb'

const props = defineProps({
  clinicName: {
    type: String,
    default: 'Klinika Przyczynowego Leczenia Niepłodności'
  },
  tab1Label: { type: String, default: 'Strona główna' },
  tab1Id: { type: String, default: 'strona-glowna' },
  tab2Label: { type: String, default: 'Nasze podejście' },
  tab2Id: { type: String, default: 'nasze-podejscie' },
  tab3Label: { type: String, default: 'Model leczenia' },
  tab3Id: { type: String, default: 'model-leczenia' },
  tab4Label: { type: String, default: 'Opinie pacjentów' },
  tab4Id: { type: String, default: 'opinie-pacjentow' },
  tab5Label: { type: String, default: 'Aktualności' },
  tab5Id: { type: String, default: 'aktualnosci' },
  tab6Label: { type: String, default: 'FAQ' },
  tab6Id: { type: String, default: 'faq' },
  tab7Label: { type: String, default: 'Kontakt' },
  tab7Id: { type: String, default: 'kontakt' },
  tab8Label: { type: String, default: '' },
  tab8Id: { type: String, default: '' },
  // eslint-disable-next-line vue/prop-name-casing
  site_context: { type: String, default: null }
})

const cardColor = computed(() => themeCardColorFor(props.site_context))

const mobileExpanded = ref(false)

const navigationTabs = computed(() => {
  const tabs = []
  for (let i = 1; i <= TAB_COUNT; i++) {
    const label = props[`tab${i}Label`]
    if (!label || !String(label).trim()) continue

    const id = (props[`tab${i}Id`] || '').trim()
    tabs.push({
      label,
      id,
      href: id ? `#${id}` : '#'
    })
  }
  return tabs
})

const activeHash = ref('')

const syncHash = () => {
  activeHash.value =
    typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : ''
}

/**
 * Scroll spy: highlight the tab whose section is currently at the top of the
 * viewport (just below the sticky header). Suspended while a click-driven
 * smooth scroll is running so the active state doesn't flicker through the
 * intermediate sections.
 */
let spyRafId = null
let programmaticScrollUntil = 0

const updateActiveFromScroll = () => {
  if (typeof window === 'undefined') return
  if (performance.now() < programmaticScrollUntil) return

  const scrollPos = window.scrollY + 2
  // Match the sticky stack currently covering the top of the viewport.
  const spyOffset = contextTabsStickyTop.value
  let activeId = ''

  for (const tab of navigationTabs.value) {
    if (!tab.id) continue

    let top
    if (isHomeAnchor(tab.id)) {
      top = 0
    } else {
      const el = document.getElementById(tab.id)
      if (!el) continue
      top = getElementScrollTop(el, { extraOffset: spyOffset })
    }

    if (scrollPos >= top) activeId = tab.id
  }

  // Snap the last section active once we hit the bottom, even if it's short.
  const scrollBottom = window.innerHeight + window.scrollY
  const nearBottom = scrollBottom >= document.documentElement.scrollHeight - 2
  if (nearBottom) {
    const lastWithId = [...navigationTabs.value].reverse().find((t) => t.id)
    if (lastWithId) activeId = lastWithId.id
  }

  if (activeId && activeId !== activeHash.value) activeHash.value = activeId
}

const onScroll = () => {
  if (spyRafId !== null) return
  spyRafId = window.requestAnimationFrame(() => {
    spyRafId = null
    updateActiveFromScroll()
  })
}

onMounted(() => {
  syncHash()
  window.addEventListener('hashchange', syncHash)
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll)
  updateActiveFromScroll()
})

onBeforeUnmount(() => {
  window.removeEventListener('hashchange', syncHash)
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('resize', onScroll)
  if (spyRafId !== null) window.cancelAnimationFrame(spyRafId)
})

const isActiveTab = (tab, index) => {
  if (activeHash.value) return tab.id === activeHash.value
  return index === 0
}

const tabStyle = (tab, index) => {
  if (isActiveTab(tab, index)) {
    return { color: ACTIVE_TEXT_COLOR, borderColor: ACTIVE_BORDER_COLOR }
  }
  return { color: INACTIVE_TEXT_COLOR, borderColor: INACTIVE_BORDER_COLOR }
}

const scrollToTab = (tab) => {
  if (isHomeAnchor(tab.id)) {
    smoothScrollToTop()
    return
  }

  const target = document.getElementById(tab.id)
  if (!target) return

  // Reserve part of the main-header height when it is shown, or when scrolling
  // up (which reveals it). Full height felt like too much gap under the sticky
  // stack; half keeps the section a bit higher without overshooting.
  const baseTop = getElementScrollTop(target)
  const scrollingUp = baseTop < window.scrollY - 1
  const extraOffset =
    mainHeaderRevealed.value || scrollingUp
      ? Math.round(mainHeaderScrollOffset() / 2)
      : 0

  smoothScrollToElement(target, { extraOffset })
}

const onTabClick = async (event, tab) => {
  if (!tab.id || typeof window === 'undefined') return

  event.preventDefault()

  const wasExpanded = mobileExpanded.value
  mobileExpanded.value = false

  if (wasExpanded) {
    await nextTick()
    await new Promise((resolve) => afterLayoutSettle(resolve))
  }

  // Suspend the scroll spy while the animated scroll runs, then hand control
  // back once it settles so scrolling away updates the active tab again.
  programmaticScrollUntil = performance.now() + SMOOTH_SCROLL_MS + 150

  scrollToTab(tab)

  const hash = `#${tab.id}`
  window.history.replaceState(null, '', hash)
  activeHash.value = tab.id
}
</script>
